import { AlertTriangle, Info, AlertCircle } from 'lucide-react';
import type { ActionItem } from '@maker-toolchest/shared';
import { EmptyState } from '@/components/EmptyState';

interface ActionItemsPanelProps {
  actionItems: ActionItem[];
  isLoading?: boolean;
  error?: Error | null;
}

export function ActionItemsPanel({ actionItems, isLoading, error }: ActionItemsPanelProps) {
  if (isLoading) {
    return (
      <div className="rounded border border-border bg-background-subtle p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Action Items</h2>
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-border bg-background-subtle p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Action Items</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
            <p className="text-sm text-foreground">Failed to load action items</p>
            <p className="text-xs text-foreground-muted mt-1">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-background-subtle p-4">
      <h2 className="mb-3 text-sm font-medium text-foreground">Action Items</h2>

      {actionItems.length === 0 ? (
        <EmptyState
          icon={<Info className="h-10 w-10" />}
          title="All clear"
          description="No action items at this time. Your inventory is looking good!"
        />
      ) : (
        <div className="space-y-2">
          {actionItems.map((item) => (
            <ActionItemCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ActionItemCardProps {
  item: ActionItem;
}

function ActionItemCard({ item }: ActionItemCardProps) {
  const severityConfig = {
    info: {
      icon: <Info className="h-4 w-4" />,
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-500',
      borderColor: 'border-blue-500/20',
    },
    warning: {
      icon: <AlertTriangle className="h-4 w-4" />,
      bgColor: 'bg-amber-500/10',
      iconColor: 'text-amber-500',
      borderColor: 'border-amber-500/20',
    },
    error: {
      icon: <AlertCircle className="h-4 w-4" />,
      bgColor: 'bg-red-500/10',
      iconColor: 'text-red-500',
      borderColor: 'border-red-500/20',
    },
  };

  const config = severityConfig[item.severity];

  return (
    <div
      className={`
        flex items-start gap-3 rounded border ${config.borderColor}
        ${config.bgColor} p-3 transition-colors
      `}
    >
      <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded ${config.iconColor}`}>
        {config.icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{item.title}</p>
        <p className="text-xs text-foreground-muted">{item.description}</p>
      </div>
    </div>
  );
}
